import { useIsMobile } from "@/hooks/use-media-query";
import { SessionTile } from "./session-tile";

export interface OpenSlot {
  /** Stable id for React key — doesn't change when sessionId is assigned */
  key: string;
  /** Null = new (not yet started) session */
  sessionId: string | null;
}

interface SessionGridProps {
  deskId: string;
  canAgent: boolean;
  slots: OpenSlot[];
  focusedKey: string | null;
  onFocus: (key: string) => void;
  /** Tile mutation: a null-slot session has been created on the server */
  onSessionCreated: (key: string, sessionId: string) => void;
  /** Tile mutation: user asked to reset this slot to a fresh blank session */
  onResetSlotToNew: (key: string) => void;
  /** Tile mutation: remove this slot from the grid entirely */
  onCloseSlot: (key: string) => void;
  onOpenFile: (path: string, name: string) => void;
}

/**
 * Up to 6 session tiles packed into a CSS grid. Column count auto-adjusts
 * to tile count; each tile is fully interactive (its own composer, its own
 * status band). A "focused" tile gets a subtle ring but all tiles remain
 * usable — the focus marker is purely visual, a visited-recently hint.
 */
export function SessionGrid({
  deskId,
  canAgent,
  slots,
  focusedKey,
  onFocus,
  onSessionCreated,
  onResetSlotToNew,
  onCloseSlot,
  onOpenFile,
}: SessionGridProps) {
  const count = slots.length;
  const isMobile = useIsMobile();

  if (count === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-2">
          <img
            src="/frogs/sleeping.gif"
            alt=""
            className="w-14 h-14 mx-auto opacity-40"
          />
          <p
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "var(--text-lg)",
              color: "var(--t3)",
            }}
          >
            No sessions open
          </p>
          <p
            className="text-[12px] max-w-sm mx-auto"
            style={{ color: "var(--t4)" }}
          >
            Pick a session from the tab bar above, or click{" "}
            <span className="font-mono" style={{ color: "var(--accent)" }}>
              + new
            </span>{" "}
            to start a fresh conversation.
          </p>
        </div>
      </div>
    );
  }

  // Grid packing:
  //   mobile: always 1 col (vertical stack, comfortable min-height per tile)
  //   1      → 1 col
  //   2      → 2 cols
  //   3,4    → 2 cols (2×2 for 4)
  //   5,6    → 3 cols (3×2)
  // Rows auto-flow.
  const cols = isMobile
    ? 1
    : count === 1
      ? 1
      : count === 2
        ? 2
        : count <= 4
          ? 2
          : 3;

  return (
    <div
      className={`h-full grid gap-3 p-3 ${isMobile ? "overflow-y-auto" : "overflow-hidden"}`}
      data-testid="session-grid"
      style={{
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        // On desktop tiles share the grid evenly; on mobile we prefer a
        // readable height per tile and let the container scroll vertically.
        gridAutoRows: isMobile ? "minmax(60vh, auto)" : "1fr",
      }}
    >
      {slots.map((slot) => (
        <div
          key={slot.key}
          id={`slot-${slot.key}`}
          className="min-h-0 min-w-0"
          style={{ height: "100%" }}
        >
          <SessionTile
            deskId={deskId}
            sessionId={slot.sessionId}
            canAgent={canAgent}
            focused={focusedKey === slot.key}
            onFocus={() => onFocus(slot.key)}
            onSessionCreated={(sid) => onSessionCreated(slot.key, sid)}
            onRequestNewSession={() => onResetSlotToNew(slot.key)}
            onClose={() => onCloseSlot(slot.key)}
            onOpenFile={onOpenFile}
          />
        </div>
      ))}
    </div>
  );
}
